import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFlag,
  faCheck,
  faTimes,
  faEye,
  faEyeSlash,
  faLightbulb,
} from "@fortawesome/free-solid-svg-icons";
import imagesrc from "./CTF/logo.png";

const SubmitCtf = () => {
  const [submittedFlag, setSubmittedFlag] = useState("");
  const [isCorrect, setIsCorrect] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const challengeTextAreaRef = useRef(null);

  const correctFlagHash =
    "959d41daf7019cee841eae48ab0dc5aaf56bd69ec70bf4ebae5b94fe8fbf8be2";

  //function to hash submitted flag
  async function hashFlag(input) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input.trim());
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    console.log(hashHex);
    return hashHex;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!submittedFlag.trim()) return;

    setIsSubmitting(true);

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 800));

    const hashedInput = await hashFlag(submittedFlag);
    const isFlagCorrect = hashedInput === correctFlagHash;

    setIsCorrect(isFlagCorrect);
    if (!isFlagCorrect) setAttempts((prev) => prev + 1);
    else setAttempts(0);
    setIsSubmitting(false);
  };

  const resetChallenge = () => {
    setSubmittedFlag("");
    setIsCorrect(null);
  };

  const containerStyle = {
    height: "100%",
    backgroundColor: isCorrect ? "#00ff88" : "#0a0a0a", // Now only affects this container
    display: "flex",
    flexDirection: "column",
    fontFamily: '"Courier New", monospace',
    color: isCorrect ? "#000" : "#00ff88",
    transition: "all 0.5s ease",
    overflow: isCorrect ? "hidden" : "none",
    padding: "10px 0 20px 0",
  };

  const textAreaContainerStyle = {
    display: "flex",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    padding: "35px 0px 35px 0px",
    background: isCorrect
      ? "#00ff88"
      : "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)",
    borderBottom: isCorrect ? "none" : "2px solid #00ff88",
    position: "relative",
    transition: "all 0.5s ease",
  };

  const challengeTextAreaStyle = {
    minWidth: "80%",
    minHeight: "100%",
    resize: "none",
    border: "3px solid #00ff88",
    borderRadius: "8px",
    boxShadow: "0 0 30px rgba(0, 255, 136, 0.3)",
    filter: isCorrect ? "grayscale(0.5) blur(2px)" : "none",
    transition: "all 0.5s ease",
    backgroundColor: "#252526",
    color: "white",
    padding: "15px",
    outline: "none",
    fontFamily: '"Courier New", monospace',
    fontSize: "14px",
    lineHeight: "1.5",
  };

  const formContainerStyle = {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    backgroundColor: isCorrect ? "#00ff88" : "rgba(10, 10, 10, 0.9)",
    backdropFilter: isCorrect ? "none" : "blur(10px)",
    transition: "all 0.5s ease",
  };

  const formStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "15px",
    width: "100%",
    maxWidth: "500px",
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    fontSize: "16px",
    backgroundColor: "rgba(0, 255, 136, 0.1)",
    border: `2px solid ${isCorrect === false ? "#ff0033" : "#00ff88"}`,
    borderRadius: "6px",
    color: "#00ff88",
    fontFamily: '"Courier New", monospace',
    outline: "none",
    transition: "all 0.3s ease",
  };

  const buttonStyle = {
    padding: "12px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    backgroundColor: "#00ff88",
    color: "#0a0a0a",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: '"Courier New", monospace',
    textTransform: "uppercase",
    letterSpacing: "1px",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const messageStyle = {
    marginTop: "10px",
    padding: "10px",
    borderRadius: "4px",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "14px",
    backgroundColor: isCorrect
      ? "rgba(0, 255, 136, 0.2)"
      : "rgba(255, 0, 51, 0.2)",
    border: `1px solid ${isCorrect ? "#00ff88" : "#ff0033"}`,
    color: isCorrect ? "#00ff88" : "#ff0033",
  };

  const hintStyle = {
    marginTop: "10px",
    padding: "10px",
    backgroundColor: "rgba(0, 255, 136, 0.1)",
    border: "1px solid #00ff88",
    borderRadius: "4px",
    fontSize: "12px",
    color: "#00ff88",
    textAlign: "center",
  };

  const successOverlayStyle = {
    position: "absolute", // Changed from 'fixed' to 'absolute'
    // top: 0,
    // left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "#00ff88",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    //zIndex: 10,
    color: "#000",
    textAlign: "center",
    //padding: "20px",
    //marginBottom: "10px",
  };

  const textAreContent = `A message, hidden beneath layers of transformation.
Four steps were taken to bury the truth.
Four steps must be taken to unearth it.

The whispers say:

    First strip the base that's sixty-four,
    Then shift thirteen, as done before,
    Reverse your prize from end to start,
    And shift thirteen to finish your part.

And the whispers reveal a message:
   fWduMWh0M20wc19uNDNtX240Y19zMDRoY3tnYWxm

Find the order within.
`;

  return (
    <div style={containerStyle}>
      {/* Success Overlay */}
      {isCorrect && (
        <div style={successOverlayStyle}>
          <FontAwesomeIcon
            icon={faCheck}
            style={{ fontSize: "80px", marginBottom: "20px" }}
          />
          <h1 style={{ fontSize: "48px", marginBottom: "10px" }}>
            *Congratulations*
          </h1>
          <h2 style={{ fontSize: "24px", marginBottom: "20px" }}>
            Challenge Completed!
          </h2>
          <p style={{ fontSize: "18px", marginBottom: "30px" }}>
            You've successfully uncovered the hidden flag!
          </p>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: "#000",
              color: "#00ff88",
            }}
            onClick={resetChallenge}
          >
            <FontAwesomeIcon icon={faFlag} />
            Play Again
          </button>
        </div>
      )}
      {/* Challenge Info Overlay */}
      {!isCorrect && ( // ADD this condition
        <div
          style={{
            textAlign: "center",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: "10px 15px",
            borderRadius: "4px",
            borderBottom: "1px solid #00ff88",
          }}
        >
          <h3 style={{ margin: "0 0 5px 0", color: "#00ff88" }}>
            === ENTROPY ===
          </h3>
          <p style={{ margin: 0, fontSize: "12px", color: "#ccc" }}>
            I found this short story in an old dusty book, I wonder what it
            means...
          </p>
        </div>
      )}

      {/* Image Section - 80% of screen */}
      {!isCorrect && ( // ADD this condition
        <div style={textAreaContainerStyle}>
          <textarea
            style={challengeTextAreaStyle}
            readOnly
            value={textAreContent}
          ></textarea>
        </div>
      )}

      {/* Form Section - 20% of screen */}
      {!isCorrect && ( // ADD this condition
        <div style={formContainerStyle}>
          <form onSubmit={handleSubmit} style={formStyle}>
            <div style={{ display: "flex", gap: "10px", width: "100%" }}>
              <input
                type="text"
                value={submittedFlag}
                onChange={(e) => setSubmittedFlag(e.target.value)}
                placeholder="Enter flag: flag{...}"
                style={inputStyle}
                disabled={isSubmitting || isCorrect}
              />
              <button
                type="submit"
                style={buttonStyle}
                disabled={isSubmitting || isCorrect}
                onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
                onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
              >
                {isSubmitting ? (
                  <>
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        border: "2px solid transparent",
                        borderTop: "2px solid #0a0a0a",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    Verifying...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFlag} />
                    Submit
                  </>
                )}
              </button>
            </div>

            {/* Hint Toggle */}
            <button
              type="button"
              onClick={() => setShowHint(!showHint)}
              style={{
                ...buttonStyle,
                padding: "8px 16px",
                fontSize: "12px",
                backgroundColor: "transparent",
                color: "#00ff88",
                border: "1px solid #00ff88",
              }}
            >
              <FontAwesomeIcon icon={showHint ? faEyeSlash : faEye} />
              {showHint ? " Hide Hint" : " Show Hint"}
            </button>

            {/* Hint */}
            {showHint && (
              <div style={hintStyle}>
                <FontAwesomeIcon icon={faLightbulb} /> First in. Last out.
              </div>
            )}

            {/* Result Message */}
            {isCorrect !== null && !isCorrect && (
              <div style={messageStyle}>
                <FontAwesomeIcon
                  icon={faTimes}
                  style={{ marginRight: "8px" }}
                />
                Incorrect flag, try again! Attempts: {attempts}
              </div>
            )}

            {/* Attempts Counter */}
            {/* {attempts > 0 && isCorrect === null && (
              <div style={{ color: "#ff9933", fontSize: "12px" }}>
                Attempts: {attempts} - Keep trying!
              </div>
            )} */}
          </form>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          input::placeholder {
            color: rgba(0, 255, 136, 0.5);
          }
          
          button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none !important;
          }
          
          button:hover:not(:disabled) {
            transform: scale(1.05);
          }

        `}
      </style>
    </div>
  );
};

export default SubmitCtf;
